import { funnelLabels } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { AuthClientResponse } from '@sola/schema/dotcom/auth.ts';
import { UserLoginState } from '@sola/schema/dotcom/auth.ts';
import type { AuthSuccess } from '@sola/schema/dotcom/auth/api.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { FundProps } from '@sola/schema/lib/schema.ts';

import { deriveSubcategory } from '../optimizely-helpers.ts';
import type { OptimizelyImpressionResponse } from '../optimizely-schemas.ts';
import { OptimizelyEventKey } from '../optimizely-schemas.ts';
import {
  convertChildToCart,
  convertFundToCart,
  mapAnalyticsCartItems,
} from './mappings.ts';
import { trackEvent, trackOptimizelyEvent } from './transport.ts';
import { getTextZoomLevel } from './util.ts';

const events = {
  accountCreate: 'sign-up',
  childSearch: 'search_for_a_child',
  click: 'element_click',
  contentFeedComment: 'comment',
  contentFeedLike: 'like',
  contentListView: 'view_content_list',
  contentSelect: 'select_content',
  contentView: 'view_content',
  error: 'error_message',
  fileDownload: 'file_download',
  fundItemSelect: 'select_item',
  funnelPageView: 'funnel_page_view',
  initialPush: 'initial_push',
  itemView: 'view_item',
  itemListView: 'view_item_list',
  leadFormSuccess: 'generate_lead',
  login: 'login',
  optimizelyResults: 'optimizely_results',
  outageNotice: 'outage_notice',
  pageView: 'page_view',
  search: 'search',
};

/* eslint-disable @typescript-eslint/naming-convention */

export const trackCreateAccount = (isAccountCreatedWithEmail = false) => {
  trackEvent({
    event: events.accountCreate,
    method: isAccountCreatedWithEmail ? 'email address' : 'supporter global id',
  });
};

export const trackLogin = (auth: AuthSuccess) => {
  trackEvent({
    event: events.login,
    supporter_global_id: auth.authUserGlobalId,
    primary_contact_id: auth.authUserPrimaryContactId,
  });
};

// https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag
export const trackPageView = (experiments?: string[]) => {
  trackEvent({
    event: events.pageView,
    page_location: globalThis.location.href,
    page_title: document.title,
    send_page_view: true,
    experiments,
  });
};

// https://support.google.com/analytics/answer/9216061 — GA4 file_download
export const trackFileDownload = (fileName: string, fileUrl: string) => {
  trackEvent({
    event: events.fileDownload,
    file_name: fileName,
    link_url: fileUrl,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29025861652/Generate+Lead
const trackLeadFormSuccess = (formIdentifier: string) => {
  trackEvent({
    event: events.leadFormSuccess,
    // The name of the lead generation form
    lead_source: formIdentifier,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287630/Clicks
// @TODO: Add click tracking to lots of places.
export const trackClick = (
  label: string,
  link?: string,
  classname?: string,
  elementId?: string,
  category?: 'Link' | 'Button',
) => {
  trackEvent({
    event: events.click,
    // The inner text of the element clicked
    element_label: label,
    // The `href` attribute of the element clicked - sometimes null
    element_link: link || null,
    // The CSS class name of the element clicked - sometimes null
    element_class_name: classname || null,
    // The `id` HTML attribute of the element clicked - sometimes null
    element_id: elementId || null,
  });

  trackOptimizelyEvent({
    eventKey: OptimizelyEventKey.enum.click_engagements,
    url: globalThis.location?.href || '',
    properties: {
      Category: category || 'Button',
      Text: label,
      URL: globalThis.location?.href || '',
      Target: category === 'Link' ? link || '' : label,
      'Class Name': classname || '',
    },
  });
};

/**
 * Fire an Optimizely conversion event for a form submission.
 * Posts to /api/optimizely/events/ which calls userContext.trackEvent()
 * server-side through the Optimizely SDK.
 */
const trackOptimizelyFormSubmit = (url: string, formId: string) => {
  trackOptimizelyEvent({
    eventKey: OptimizelyEventKey.enum.form_submits,
    url,
    properties: { URL: url, Text: formId },
  });
};

/**
 * Track a successful form submission — fires both the GA generate_lead
 * dataLayer event and the Optimizely form_submits conversion event.
 * Replaces the common pattern of calling trackLeadFormSuccess +
 * trackOptimizelyFormSubmit separately with the same form ID.
 */
export const trackFormSuccess = (formId: string) => {
  trackLeadFormSuccess(formId);
  trackOptimizelyFormSubmit(globalThis.location?.href || '', formId);
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582549/Search
export const trackSearch = (searchTerm?: string) => {
  // Don't track empty strings
  if (!searchTerm) {
    return;
  }
  trackEvent({
    event: events.search,
    // The term the user searched for
    search_term: searchTerm,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026582663/Funnel+Page+View
export const trackFunnelPage = (funnel?: string | null) => {
  if (!funnel || !funnelLabels[funnel as keyof typeof funnelLabels]) {
    return;
  }
  trackEvent({
    event: events.funnelPageView,
    funnel_category: funnelLabels[funnel as keyof typeof funnelLabels],
  });
};

export const trackOptimizelyPageView = (
  pathname: string,
  funnel?: string | null,
) => {
  const category = funnel
    ? (funnelLabels[funnel as keyof typeof funnelLabels] ?? '')
    : '';
  const subcategory = deriveSubcategory(pathname);

  trackOptimizelyEvent({
    eventKey: OptimizelyEventKey.enum.page_views,
    url: globalThis.location?.href || '',
    properties: {
      Category: category,
      Subcategory: subcategory,
      URL: globalThis.location?.href || '',
    },
  });
};

// Content Feed — view_content_list
export const trackViewContentList = (
  contentListId: string,
  contentListName: string,
  contents: Array<{
    content_id: string;
    content_type: string;
    content_tags: string[];
    content_title: string;
  }>,
) => {
  trackEvent({
    event: events.contentListView,
    content_list_id: contentListId,
    content_list_name: contentListName,
    contents,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027893249/Select+Content
export const trackSelectContent = (
  cmsId: string,
  title: string,
  type: string,
  tags: string[],
) => {
  trackEvent({
    event: events.contentSelect,
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026091384/View+Content
export const trackViewContent = (
  cmsId: string,
  title: string,
  type: string,
  tags: string[],
) => {
  trackEvent({
    event: events.contentView,
    // CMS ID of the content
    content_id: cmsId,
    // Title of the content
    content_title: title,
    // Category of the content,
    content_type: type,
    // Array of tags associated with the story
    content_tags: tags,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026385954/Search+for+a+Child
export type ChildSearchEvent = {
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  region?: string;
  country?: string | string[];
  birthMonth?: number;
  birthDay?: number;
  orphan?: boolean;
  disability?: boolean;
  highlyVulnerable?: boolean;
  hiv?: boolean;
};
export const trackChildSearch = (event: ChildSearchEvent) => {
  trackEvent({
    event: events.childSearch,
    filter_gender: event.gender,
    filter_age_min: event.ageMin,
    filter_age_max: event.ageMax,
    filter_region: event.region,
    filter_country: Array.isArray(event.country)
      ? event.country.join(', ')
      : event.country,
    filter_birth_month: event.birthMonth,
    filter_birth_day: event.birthDay,
    filter_orphan: event.orphan,
    filter_disabilities: event.disability,
    filter_hvc: event.highlyVulnerable,
    filter_hiv: event.hiv,
  });
};

export const trackViewFund = (fund: FundProps) => {
  trackEvent({
    event: events.itemView,
    items: mapAnalyticsCartItems([convertFundToCart(fund)]),
    currency: 'USD',
  });
};

export const trackViewFundItemList = (
  listId: string,
  listName: string,
  funds: FundProps[],
  isCatalog: boolean,
) => {
  if (!funds?.length) {
    return;
  }

  trackEvent({
    event: events.itemListView,
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(
      funds.map((fund) => convertFundToCart(fund, isCatalog)),
    ),
    currency: 'USD',
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29027696645/Select+Item
export const trackSelectFundItem = (
  listId: string,
  listName: string,
  fund?: FundProps,
  isCatalog = false,
) => {
  if (!fund) {
    return;
  }
  trackEvent({
    event: events.fundItemSelect,
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems([convertFundToCart(fund, isCatalog)]),
    currency: 'USD',
  });
};

export const trackViewSacItemList = (
  listId: string,
  listName: string,
  items: ChildAvailabilityItem[],
) => {
  if (!items?.length) {
    return;
  }
  trackEvent({
    event: events.itemListView,
    item_list_id: listId,
    item_list_name: listName,
    items: mapAnalyticsCartItems(items.map(convertChildToCart)),
    currency: 'USD',
  });
};

export const trackViewBio = (child: ChildAvailabilityItem) => {
  trackEvent({
    event: events.itemView,
    items: mapAnalyticsCartItems([convertChildToCart(child)]),
    currency: 'USD',
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29026287637/Errors
// @TODO: Add error tracking to lots of places.
export const trackError = (errorMessage?: string) => {
  if (!errorMessage) {
    return;
  }
  trackEvent({
    event: events.error,
    // The error message presented to the user
    message: errorMessage,
  });
};

// TODO: this isn't used: remove or use?
export const trackOutageNotice = (payload: {
  severity: string;
  title?: string;
  message?: string;
  trackingId?: string;
  ctaLabel?: string;
  ctaHref?: string;
}) => {
  trackEvent({
    event: events.outageNotice,
    outage_severity: payload.severity,
    outage_title: payload.title || null,
    outage_message: payload.message || null,
    outage_tracking_id: payload.trackingId || null,
    outage_cta_label: payload.ctaLabel || null,
    outage_cta_href: payload.ctaHref || null,
  });
};

export const trackOptimizelyResults = (
  experiments: OptimizelyImpressionResponse,
) => {
  trackEvent({
    event: events.optimizelyResults,
    uid: experiments?.uid,
    results: experiments?.results,
    user_attributes: {
      ...experiments?.userAttributes,
      hostname: globalThis?.location.hostname,
    },
  });
};

// Content Feed "like" — the user adds a reaction to a post or comment.
// Fires only when a reaction is newly added (not on unlike or emoji switch).
// `content_tags` is intentionally omitted: the Content Feed API does not
// currently supply tag/channel data for feed entries.
export const trackContentFeedLike = (params: {
  contentId: string;
  contentTitle: string;
  contentUrl?: string;
  /** Human-readable name of the emoji selected (e.g. "Love", "Grateful"). */
  emoji: string;
  /** The comment id when the like targets a comment rather than the post. */
  commentId?: string;
}) => {
  trackEvent({
    event: events.contentFeedLike,
    // The unique identifier for the content that is being liked
    content_id: params.contentId,
    // The title of the content being liked
    content_title: params.contentTitle,
    // The URL of the landing page for the content
    content_url: params.contentUrl || null,
    // The name of the emoji selected
    emoji: params.emoji,
    // The comment id when the like targets a comment, else null
    comment_id: params.commentId || null,
  });
};

// Content Feed "comment" — the user comments on a post or replies to a comment.
// Fires on the success path after the comment (or reply) is persisted.
// `content_tags` is intentionally omitted: the Content Feed API does not
// currently supply tag/channel data for feed entries.
export const trackContentFeedComment = (params: {
  contentId: string;
  contentTitle: string;
  contentUrl?: string;
  /** The parent comment id when this is a reply to a comment. */
  commentId?: string;
}) => {
  trackEvent({
    event: events.contentFeedComment,
    // The unique identifier for the content that is being commented on
    content_id: params.contentId,
    // The title of the content being commented on
    content_title: params.contentTitle,
    // The URL of the landing page for the content
    content_url: params.contentUrl || null,
    // The comment id being commented on (reply target), else null
    comment_id: params.commentId || null,
  });
};

// https://ciorg.atlassian.net/wiki/spaces/DX/pages/29089169443/User+Identification
export const trackUser = (
  authUser?: AuthClientResponse | null,
  cart?: Cart | null,
) => {
  const loggedIn = !!authUser?.authUserGlobalId;
  const { ratio: textZoomRatio, isLargeText } = getTextZoomLevel();
  trackEvent({
    event: events.initialPush,
    is_logged_in: loggedIn,
    logged_in_state: loggedIn ? UserLoginState.LoggedIn : UserLoginState.Guest,
    // TODO: determine source code if no cart?
    source_code_id: cart?.sourceCode,
    supporter_global_id: authUser?.authUserGlobalId,
    user_id: authUser?.authUserPrimaryContactId,
    // Accessibility: text zoom detection
    text_zoom_ratio: textZoomRatio,
    is_large_text: isLargeText,
  });
};
